import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Form, Input, Upload, Button, Space, message, Alert, Descriptions, Tag } from 'antd';
import type { Rule } from 'antd/lib/form';
import type { RcFile, UploadFile, UploadChangeParam } from 'antd/es/upload';
import { InboxOutlined, WarningOutlined, ArrowLeftOutlined, AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { getPanel, updatePanel } from '@/api/panels';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';

interface FormValues {
  version: string;
}

/**
 * 面板升级页面（升级版本号 + 重新上传文件）
 */
export const PanelUpdate = () => {
  const { panelId } = useParams<{ panelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [versionWarning, setVersionWarning] = useState<string | null>(null);
  const developerId = getCurrentDeveloperId();

  // 版本号比较工具函数
  const isVersionGreater = (newVersion: string, currentVersion: string): boolean => {
    const [newMajor, newMinor, newPatch] = newVersion.split('.').map(Number);
    const [currMajor, currMinor, currPatch] = currentVersion.split('.').map(Number);
    
    if (newMajor > currMajor) return true;
    if (newMajor < currMajor) return false;
    
    if (newMinor > currMinor) return true;
    if (newMinor < currMinor) return false;
    
    return newPatch > currPatch;
  };

  // 获取面板详情
  const panelQuery = useQuery({
    queryKey: ['panel', developerId, panelId],
    queryFn: () => {
      if (!panelId) throw new Error('面板 ID 不能为空');
      return getPanel(panelId);
    },
    enabled: !!panelId && !!developerId,
  });

  const panel = panelQuery.data;
  const loading = panelQuery.isLoading;
  const error = panelQuery.error;

  // 设置表单初始值 (必须在条件返回前调用)
  useEffect(() => {
    if (panel) {
      form.setFieldsValue({
        version: panel.version.replace('v', ''),
      });
    }
  }, [panel, form]);

  // 更新面板 Mutation
  const updateMutation = useMutation({
    mutationFn: ({ version, file }: { version: string; file: File }) => {
      if (!panelId) throw new Error('面板 ID 不能为空');
      return updatePanel(panelId, version, file);
    },
    onSuccess: (updatedPanel) => {
      message.success(`面板升级成功！新版本: v${updatedPanel.version}`);
      queryClient.invalidateQueries({ queryKey: ['panel', developerId, panelId] });
      queryClient.invalidateQueries({ queryKey: ['panels', developerId] });
      navigate(`/panels/${panelId}`);
    },
    onError: (error: unknown) => {
      let errorMessage = '面板升级失败';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // 尝试获取Axios响应错误
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const errorWithResponse = error as { response?: { data?: { message?: string } } };
        if (errorWithResponse.response?.data?.message) {
          errorMessage = errorWithResponse.response.data.message;
        }
      }
      
      message.error(errorMessage);
    },
  });

  /**
   * 版本号验证函数
   */
  const validateVersion = (_: Rule, value: string) => {
    if (!value) {
      return Promise.reject('请输入新版本号');
    }

    // 检查版本格式 (x.x.x)
    if (!/^\d+\.\d+\.\d+$/.test(value)) {
      return Promise.reject('版本格式应为 x.x.x（例如：2.0.0）');
    }

    // 检查版本是否大于当前版本
    if (panel) {
      try {
        const currentVersion = panel.version.replace('v', '');
        const newVersion = value.replace('v', '');
        
        if (!isVersionGreater(newVersion, currentVersion)) {
          return Promise.reject(`新版本号必须大于当前版本 v${panel.version}`);
        }
      } catch {
        return Promise.reject('版本号格式无效');
      }
    }

    return Promise.resolve();
  };

  /**
   * 文件上传前验证
   */
  const beforeUpload = (file: RcFile) => {
    // 验证文件大小 (10MB 限制)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      message.error('文件大小不能超过 10MB');
      return Upload.LIST_IGNORE;
    }

    // 验证文件类型
    if (panel) {
      const fileName = file.name.toLowerCase();
      const validExtensions = panel.file_type === 'jsbundle' ? ['.jsbundle'] : ['.bundle'];
      const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType) {
        message.error(`仅支持 ${panel.file_type === 'jsbundle' ? '.jsbundle (iOS)' : '.bundle (Android)'} 格式的文件`);
        return Upload.LIST_IGNORE;
      }
    }

    // 更新选中的文件
    setSelectedFile(file);
    return false; // 阻止自动上传
  };

  /**
   * 处理文件变化
   */
  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    // 只保留最后一个文件
    const latestFile = info.fileList[info.fileList.length - 1];
    setFileList(latestFile ? [latestFile] : []);
    
    // 如果文件被移除，清除选中的文件
    if (info.file.status === 'removed') {
      setSelectedFile(null);
    }
  };

  /**
   * 处理版本号变化
   */
  const handleVersionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (panel) {
      try {
        const currentVersion = panel.version.replace('v', '');
        const newVersion = newValue.replace('v', '');
        
        if (!isVersionGreater(newVersion, currentVersion)) {
          setVersionWarning(`新版本号必须大于当前版本 v${panel.version}`);
        } else {
          setVersionWarning(null);
        }
      } catch {
        setVersionWarning('版本号格式无效');
      }
    }
  };

  /**
   * 处理表单提交
   */
  const onFinish = async (values: FormValues) => {
    if (!selectedFile) {
      message.error('请选择要上传的面板文件');
      return;
    }

    await updateMutation.mutateAsync({
      version: values.version,
      file: selectedFile
    });
  };

  /**
   * 处理返回详情
   */
  const handleBack = () => {
    if (panelId) {
      navigate(`/panels/${panelId}`);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载面板信息，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  if (!panel) {
    return (
      <ErrorAlert 
        message="未找到面板" 
        description="指定的面板不存在或已被删除"
        className="mb-8"
      />
    );
  }

  // 获取当前文件类型
  const currentFileType = panel.file_type === 'jsbundle' ? '.jsbundle' : '.bundle';
  const platformName = panel.file_type === 'jsbundle' ? 'iOS' : 'Android';
  const platformIcon = panel.file_type === 'jsbundle' ? <AppleOutlined /> : <AndroidOutlined />;
  const platformColor = panel.file_type === 'jsbundle' ? 'blue' : 'green';
  const fileTypeDesc = panel.file_type === 'jsbundle' 
    ? '.jsbundle (iOS 应用)'
    : '.bundle (Android 应用)';
  const fileTypeIcon = panel.file_type === 'jsbundle' ? '📱' : '🤖';

  return (
    <div className="w-full space-y-8">
      {/* 标题+返回按钮 - 统一层级 */}
      <div className="flex flex-wrap justify-between items-center gap-4 max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <WarningOutlined className={`text-${platformColor}-500`} />
            升级面板 - {panel.name}
          </h1>
          <p className="text-gray-500 mt-1">升级面板版本号并重新上传文件，升级后关联产品将自动使用新版本。</p>
        </div>
        <Button 
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回详情
        </Button>
      </div>

      <div className="max-w-2xl mx-auto w-full">
        {/* 当前面板信息卡片 */}
        <Card 
          title="当前面板信息"
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 mb-8"
          style={{ 
            padding: '20px',
            '--ant-card-head-font-size': '16px',
            '--ant-card-head-font-weight': 600
          } as React.CSSProperties}
        >
          <Descriptions column={1} bordered={false} size="middle">
            <Descriptions.Item label="面板ID" labelStyle={{ width: '140px', fontWeight: 600 }}>
              <span className="font-mono">{panel.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="适配平台" labelStyle={{ width: '140px', fontWeight: 600 }}>
              <Tag color={platformColor}>{platformIcon} {platformName}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="当前版本" labelStyle={{ width: '140px', fontWeight: 600 }}>
              <span className="text-purple-600 font-medium">v{panel.version}</span>
            </Descriptions.Item>
            <Descriptions.Item label="文件类型" labelStyle={{ width: '140px', fontWeight: 600 }}>
              <span className="font-mono">{fileTypeDesc}</span>
            </Descriptions.Item>
            <Descriptions.Item label="文件大小" labelStyle={{ width: '140px', fontWeight: 600 }}>
              {panel.file_size ? `${(panel.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <br />

        {/* 升级面板卡片 */}
        <Card 
          title="面板升级配置"
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
          style={{ padding: '0px' } as React.CSSProperties}
        >
          <Alert
            message="升级须知"
            description={
              <>
                <p className="mb-2">1. <strong>新版本号必须严格大于当前版本号</strong>，例如从 v1.2.3 升级到 v1.2.4 或 v2.0.0</p>
                <p className="mb-2">2. <strong>文件类型必须与当前面板一致</strong>：只能上传 {fileTypeDesc} 文件</p>
                <p className="mb-2">3. <strong>文件大小限制 10MB</strong>，超出将无法上传。</p>
                <p>4. <strong>升级后旧文件将被覆盖</strong>，所有关联此面板的产品将自动使用新版本。</p>
              </>
            }
            type="warning"
            showIcon
            className="mb-8 rounded-lg"
          />

          <br />

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="space-y-8"
          >
            <Form.Item
              name="version"
              label="新版本号"
              rules={[{ validator: validateVersion }]}
              help={versionWarning && (
                <span className="text-orange-500 flex items-center mt-1">
                  <WarningOutlined className="mr-1" />
                  {versionWarning}
                </span>
              )}
              extra={`推荐升级：${parseInt(panel.version.split('.')[0]) + 1}.0.0（主版本）或 ${panel.version.split('.')[0]}.${parseInt(panel.version.split('.')[1]) + 1}.0（次版本）`}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="large"
                placeholder={`输入新版本号，如 ${parseInt(panel.version.split('.')[0]) + 1}.0.0`}
                onChange={handleVersionChange}
                disabled={updateMutation.isPending}
                style={{ borderRadius: '8px' } as React.CSSProperties}
              />
            </Form.Item>

            <br />

            <Form.Item
              label={`新面板文件 · ${platformName} (${fileTypeDesc})`}
              rules={[{ required: true, message: '请选择或拖拽面板文件' }]}
              validateStatus={selectedFile ? 'success' : (updateMutation.isPending ? 'validating' : 'error')}
              help={!selectedFile 
                ? <span className="text-red-500">请选择或拖拽要上传的面板文件</span>
                : <span className="text-green-500 flex items-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {selectedFile.name} · {Math.round(selectedFile.size / 1024)} KB
                  </span>
              }
              style={{ marginBottom: 0 }}
            >
              <Upload.Dragger
                accept={currentFileType}
                multiple={false}
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleChange}
                onRemove={() => setSelectedFile(null)}
                disabled={updateMutation.isPending}
                className="rounded-lg"
                style={{ 
                  border: selectedFile ? '2px solid #52c41a' : '2px dashed #d9d9d9',
                  background: selectedFile ? '#f0f9eb' : 'transparent',
                  transition: 'all 0.3s'
                } as React.CSSProperties}
              >
                {selectedFile ? (
                  <div className="flex flex-col items-center p-8 text-center">
                    <div className="text-6xl mb-4">{fileTypeIcon}</div>
                    <div className="font-medium text-lg mb-2 break-all max-w-full text-gray-800">
                      {selectedFile.name}
                    </div>
                    <div className="text-gray-600 text-sm mb-3">
                      {Math.round(selectedFile.size / 1024)} KB · {currentFileType}
                    </div>
                    <div className="text-xs text-gray-500">
                      点击或拖拽其他文件可替换
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-5xl text-gray-400 mb-4">
                      <InboxOutlined />
                    </div>
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      点击或拖拽文件到此区域上传
                    </p>
                    <p className="text-gray-500 text-sm">
                      仅支持 {fileTypeDesc} 格式 · 最大 10MB
                    </p>
                  </div>
                )}
              </Upload.Dragger>
            </Form.Item>

            <br />

            <Form.Item className="mt-8 mb-0">
              <Space size="large" style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button
                  size="large"
                  onClick={handleBack}
                  disabled={updateMutation.isPending}
                  style={{ borderRadius: '8px' } as React.CSSProperties}
                >
                  取消
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={updateMutation.isPending}
                  disabled={updateMutation.isPending || !selectedFile}
                  className="px-8"
                  style={{ borderRadius: '8px' } as React.CSSProperties}
                >
                  {updateMutation.isPending ? '升级中...' : '确认升级'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};